import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type Candidate, type GateResults, getOptionLabel, getOptionIcon } from '../lib/api';
import { fmt } from '../lib/format';

/** Lolos = reject_reason null/empty */
function isPassed(c: Candidate): boolean {
  const r = c.reject_reason;
  return r == null || String(r).trim() === '';
}

const scoreColor = (s: number) =>
  s < 30 ? 'var(--gl-emerald)' : s < 60 ? 'var(--gl-amber)' : 'var(--gl-rose)';
const scoreBg = (s: number) =>
  s < 30 ? 'rgba(52,211,153,0.1)' : s < 60 ? 'rgba(251,191,36,0.1)' : 'rgba(251,113,133,0.1)';

type CandidatesRes = {
  candidates: Candidate[];
  count: number;
  cycle_id?: string | null;
  stats?: { total: number; passed: number; rejected: number };
};

// ===== CHIP GATE =====
/** Warna chip berdasarkan status gate */
function gateChipColor(passed: boolean | null | undefined): string {
  if (passed === true) return 'var(--gl-emerald)';
  if (passed === false) return 'var(--gl-rose)';
  return 'var(--gl-text-faint)';
}
function gateChipBg(passed: boolean | null | undefined): string {
  if (passed === true) return 'rgba(52,211,153,0.12)';
  if (passed === false) return 'rgba(251,113,133,0.12)';
  return 'rgba(255,255,255,0.04)';
}
function gateChipLabel(passed: boolean | null | undefined, label: string): string {
  if (passed === true) return label;
  if (passed === false) return `✕ ${label}`;
  return '—';
}

/** Satu chip gate */
function GateChip({ passed, label, title }: { passed: boolean | null | undefined; label: string; title?: string }) {
  return (
    <span
      title={title || label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: '1px 7px',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 600,
        lineHeight: '18px',
        color: gateChipColor(passed),
        background: gateChipBg(passed),
        border: `1px solid ${gateChipColor(passed)}33`,
        transition: 'all 0.15s',
      }}
    >
      {gateChipLabel(passed, label)}
    </span>
  );
}

/** Kumpulan 4 chip gate untuk 1 kandidat */
function GateChips({ c }: { c: Candidate }) {
  // Urutan gate: macro → liquidation → min_distance → soft_score
  // Kalau gagal di gate 1, gate 2 & 3 di-skip
  const gp = c.gate_passed;
  // Parse reject_reason untuk tau soft score fail
  const isSoftFail = c.reject_reason != null && String(c.reject_reason).startsWith('soft_score');

  // Logic gate: macro, liquidation, distance selalu dihitung oleh engine
  // Soft score hanya dihitung kalau semua gate lolos
  const gate2skip = gp.macro === false;
  const gate3skip = gp.macro === false || gp.liquidation_zone === false;
  const softSkipped = gp.macro === false || gp.liquidation_zone === false || gp.min_distance === false;

  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
      <GateChip passed={gp.macro} label="📅" title={`Macro: ${gp.macro ? 'OK' : 'Gagal'}`} />
      <GateChip passed={gate2skip ? null : gp.liquidation_zone} label="🔥" title={`Likuidasi: ${gp.liquidation_zone ? 'OK' : 'Gagal'}`} />
      <GateChip passed={gate3skip ? null : gp.min_distance} label="📏" title={`Jarak: ${gp.min_distance ? 'OK' : 'Gagal'}`} />
      <GateChip
        passed={softSkipped ? null : !isSoftFail}
        label="📊"
        title={softSkipped ? 'Skip (gagal gate)' : isSoftFail ? 'Score > threshold' : `Score ${c.soft_score}`}
      />
    </div>
  );
}

// ===== Detail Modal =====
function GateDetailModal({ c, onClose }: { c: Candidate; onClose: () => void }) {
  const gr = c.gate_results;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, cursor: 'pointer',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="g-card"
        style={{ maxWidth: 560, width: '100%', padding: 24, maxHeight: '90vh', overflowY: 'auto', cursor: 'default' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <strong style={{ fontSize: 16 }}>Gate Detail</strong>
            <div style={{ fontSize: 12, color: 'var(--gl-text-muted)', marginTop: 4 }}>
              {c.underlying} {c.option_type} Strike {fmt.number(c.strike_price, 0)} · Score {fmt.score(c.soft_score)}
            </div>
          </div>
          <button className="glass-btn" style={{ padding: '4px 12px', fontSize: 12 }} onClick={onClose}>✕</button>
        </div>

        {/* Gate 1: Macro */}
        <div className="g-card" style={{ padding: 14, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong>📅 Gate 1 — Macro Event</strong>
            <span style={{ fontSize: 11, fontWeight: 600, color: c.gate_passed.macro ? 'var(--gl-emerald)' : 'var(--gl-rose)' }}>
              {c.gate_passed.macro ? '✅ PASSED' : '❌ FAILED'}
            </span>
          </div>
          {gr?.macro ? (
            <div style={{ fontSize: 12, color: 'var(--gl-text-muted)', lineHeight: 1.6 }}>
              <div>Settle: {fmt.dateFull(gr.macro.settle_date)}</div>
              <div>Buffer: ±{gr.macro.buffer_hours} jam</div>
              {gr.macro.nearest_event ? (
                <div style={{ color: gr.macro.passed ? 'var(--gl-text-muted)' : 'var(--gl-rose)' }}>
                  Event terdekat: <strong>{gr.macro.nearest_event.name}</strong> ({gr.macro.nearest_event.diff_hours} jam)
                </div>
              ) : (
                <div>Tidak ada event high-impact ±6 jam</div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--gl-text-faint)' }}>Detail tidak tersedia</div>
          )}
        </div>

        {/* Gate 2: Liquidation */}
        <div className="g-card" style={{ padding: 14, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong>🔥 Gate 2 — Liquidation Zone</strong>
            <span style={{ fontSize: 11, fontWeight: 600, color: c.gate_passed.liquidation_zone ? 'var(--gl-emerald)' : 'var(--gl-rose)' }}>
              {c.gate_passed.liquidation_zone ? '✅ PASSED' : '❌ FAILED'}
            </span>
          </div>
          {gr?.liquidation_zone ? (
            <div style={{ fontSize: 12, color: 'var(--gl-text-muted)', lineHeight: 1.6 }}>
              <div>Strike zone: {fmt.number(gr.liquidation_zone.strike_zone_from, 0)} — {fmt.number(gr.liquidation_zone.strike_zone_to, 0)} (toleransi {gr.liquidation_zone.tolerance_pct}%)</div>
              <div>Min intensity: {gr.liquidation_zone.min_intensity}</div>
              {gr.liquidation_zone.nearest_cluster ? (
                <div>
                  Cluster terdekat: {fmt.number(gr.liquidation_zone.nearest_cluster.price, 0)} (intensity {gr.liquidation_zone.nearest_cluster.intensity}, {gr.liquidation_zone.nearest_cluster.side})
                </div>
              ) : (
                <div>Tidak ada cluster likuidasi</div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--gl-text-faint)' }}>Detail tidak tersedia</div>
          )}
        </div>

        {/* Gate 3: Min Distance */}
        <div className="g-card" style={{ padding: 14, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong>📏 Gate 3 — Min Distance (ATR)</strong>
            <span style={{ fontSize: 11, fontWeight: 600, color: c.gate_passed.min_distance ? 'var(--gl-emerald)' : 'var(--gl-rose)' }}>
              {c.gate_passed.min_distance ? '✅ PASSED' : '❌ FAILED'}
            </span>
          </div>
          {gr?.min_distance ? (
            <div style={{ fontSize: 12, color: 'var(--gl-text-muted)', lineHeight: 1.6 }}>
              <div>Spot: {fmt.number(gr.min_distance.atr_14d ? gr.min_distance.required / gr.min_distance.multiplier * 100 : 0, 0)} (est dari ATR)</div>
              <div>ATR(14): {fmt.number(gr.min_distance.atr_14d, 2)}</div>
              <div>Jarak: {fmt.number(gr.min_distance.distance, 0)} | Required: {fmt.number(gr.min_distance.required, 0)} ({gr.min_distance.multiplier}×ATR)</div>
              <div style={{ color: gr.min_distance.passed ? 'var(--gl-emerald)' : 'var(--gl-rose)', marginTop: 4 }}>
                {gr.min_distance.passed
                  ? `${fmt.number(gr.min_distance.distance, 0)} ≥ ${fmt.number(gr.min_distance.required, 0)} ✅`
                  : `${fmt.number(gr.min_distance.distance, 0)} < ${fmt.number(gr.min_distance.required, 0)} ❌`}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--gl-text-faint)' }}>Detail tidak tersedia</div>
          )}
        </div>

        {/* Reject reason */}
        {c.reject_reason && (
          <div className="g-card" style={{ padding: 14, fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--gl-rose)' }}>
            reject_reason: {c.reject_reason}
          </div>
        )}
      </div>
    </div>
  );
}

// ===== View Toggle Tab =====
function ViewTabs({ value, onChange }: { value: 'list' | 'pairs'; onChange: (v: 'list' | 'pairs') => void }) {
  const tab = (key: 'list' | 'pairs', label: string) => (
    <button
      onClick={() => onChange(key)}
      style={{
        padding: '8px 20px', fontSize: 13, fontWeight: 600,
        color: value === key ? 'var(--gl-text)' : 'var(--gl-text-muted)',
        background: 'transparent', border: 'none',
        borderBottom: value === key ? '2px solid var(--gl-primary)' : '2px solid transparent',
        cursor: 'pointer', transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );
  return (
    <div style={{
      display: 'flex', gap: 0, marginBottom: 20,
      borderBottom: '1px solid var(--gl-border)',
    }}>
      {tab('list', 'List View')}
      {tab('pairs', 'Pair View')}
    </div>
  );
}

// ===== Pair Analytics Card =====
function PairCard({ pair, stats, totalCandidates }: { pair: string; stats: { total: number; passed: number; scores: number[]; aprs: number[] }; totalCandidates: number }) {
  const avgScore = stats.scores.length ? stats.scores.reduce((a, b) => a + b, 0) / stats.scores.length : 0;
  const avgApr = stats.aprs.length ? stats.aprs.reduce((a, b) => a + b, 0) / stats.aprs.length : 0;
  const winRate = stats.total > 0 ? (stats.passed / stats.total) * 100 : 0;
  // Estimasi jumlah cycle yang tercakup
  const cycleEst = Math.max(1, Math.round(totalCandidates / 400));
  return (
    <div className="g-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--gl-text)', marginBottom: 12 }}>
        {pair}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13 }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Total scan</div>
          <div style={{ fontWeight: 600, color: 'var(--gl-text)', marginTop: 2 }}>{stats.total}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Lolos</div>
          <div style={{ fontWeight: 600, color: 'var(--gl-emerald)', marginTop: 2 }}>{stats.passed}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Win rate</div>
          <div style={{ fontWeight: 600, color: winRate > 10 ? 'var(--gl-emerald)' : 'var(--gl-amber)', marginTop: 2 }}>
            {winRate.toFixed(1)}%
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Rata² score</div>
          <div style={{ fontWeight: 600, color: 'var(--gl-text)', marginTop: 2 }}>{avgScore.toFixed(1)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Rata² APR</div>
          <div style={{ fontWeight: 600, color: 'var(--gl-emerald)', marginTop: 2 }}>{avgApr.toFixed(1)}%</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Cycle cakupan</div>
          <div style={{ fontWeight: 600, color: 'var(--gl-text)', marginTop: 2 }}>~{cycleEst}</div>
        </div>
      </div>
    </div>
  );
}

// ===== Main Page =====
export function CandidatesPage() {
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'pairs'>('list');

  // Single-cycle query untuk List View
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['candidates', 'latest-cycle'],
    queryFn: () => api.get<CandidatesRes>('/api/bot/candidates?limit=400&cycle_id=latest'),
    refetchInterval: 30_000,
  });

  // Multi-cycle query untuk Pair View (hanya jalan kalau tab pairs aktif)
  const { data: pairData, isLoading: pairLoading } = useQuery({
    queryKey: ['candidates', 'pairs'],
    queryFn: () => api.get<{ candidates: Candidate[]; count: number }>('/api/bot/candidates?limit=1000'),
    enabled: viewMode === 'pairs',
    refetchInterval: 60_000,
  });

  // Derived: list view
  const candidates = data?.candidates || [];
  const stats = data?.stats;
  const passed = candidates.filter(isPassed);
  const rejected = candidates.filter((c) => !isPassed(c));
  const passedSorted = [...passed].sort((a, b) => Number(a.soft_score) - Number(b.soft_score));
  const total = stats?.total ?? candidates.length;
  const passedN = stats?.passed ?? passed.length;
  const rejectedN = stats?.rejected ?? rejected.length;

  // Derived: pair view
  const pairs = useMemo(() => {
    if (!pairData?.candidates) return [];
    const byPair: Record<string, { total: number; passed: number; scores: number[]; aprs: number[] }> = {};
    for (const c of pairData.candidates) {
      const pair = c.underlying;
      if (!byPair[pair]) byPair[pair] = { total: 0, passed: 0, scores: [], aprs: [] };
      byPair[pair].total++;
      const p = c.reject_reason == null || String(c.reject_reason).trim() === '';
      if (p) {
        byPair[pair].passed++;
        byPair[pair].scores.push(Number(c.soft_score));
        byPair[pair].aprs.push(Number(c.apr));
      }
    }
    return Object.entries(byPair).sort((a, b) => b[1].total - a[1].total);
  }, [pairData]);

  return (
    <div className="gl-page">
      {selectedCandidate && (
        <GateDetailModal c={selectedCandidate} onClose={() => setSelectedCandidate(null)} />
      )}

      {/* View toggle tab */}
      <ViewTabs value={viewMode} onChange={setViewMode} />

      {viewMode === 'list' ? (
        /* ===== LIST VIEW ===== */
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--gl-text)' }}>Rekomendasi Nevi</div>
              <div style={{ fontSize: 12, color: 'var(--gl-text-muted)', marginTop: 4, lineHeight: 1.45 }}>
                Indopoin · hasil filter cycle terakhir · chip gate per kandidat (🟢=lolos 🔴=gagal ⚫=skip)
              </div>
              {data?.cycle_id && (
                <div style={{ fontSize: 11, color: 'var(--gl-text-faint)', fontFamily: 'var(--font-mono)', marginTop: 6 }}>
                  cycle: {data.cycle_id}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Scan</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--gl-text)' }}>{total}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Lolos</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--gl-emerald)' }}>{passedN}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Ditolak</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--gl-rose)' }}>{rejectedN}</div>
              </div>
              <button
                type="button"
                className="glass-btn"
                style={{ padding: '8px 12px', fontSize: 12 }}
                onClick={() => refetch()}
                disabled={isFetching}
              >
                {isFetching ? '…' : 'Refresh'}
              </button>
            </div>
          </div>

          {error && (
            <div className="settings-alert settings-alert--error" style={{ marginBottom: 16 }}>
              {(error as Error).message}
            </div>
          )}

          {/* LEGEND */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: 11, color: 'var(--gl-text-muted)', alignItems: 'center' }}>
            <span>Chip:</span>
            <span style={{ color: 'var(--gl-emerald)' }}>🟢 = Lolos</span>
            <span style={{ color: 'var(--gl-rose)' }}>🔴 = Gagal</span>
            <span style={{ color: 'var(--gl-text-faint)' }}>⚫ = Skip</span>
            <span style={{ marginLeft: 12 }}>Klik baris untuk detail gate.</span>
          </div>

          {/* PASSED */}
          <div className="g-card rvl" style={{ marginBottom: 16 }}>
            <div className="gl-label">
              Lolos / rekomendasi{' '}
              <span style={{ color: 'var(--gl-text-faint)', fontWeight: 400 }}>
                · {passedN} produk · skor makin rendah makin aman
              </span>
            </div>
            {isLoading ? (
              <div className="gl-empty">Memuat…</div>
            ) : passedSorted.length === 0 ? (
              <div className="gl-empty">
                Tidak ada yang lolos di cycle ini.
                <div style={{ marginTop: 8, fontSize: 12 }}>
                  Cek rejected di bawah, atau buka <Link to="/kalender">Kalender</Link> /{' '}
                  <Link to="/">Ringkasan</Link> lalu minta Nevi analisis lagi.
                </div>
              </div>
            ) : (
              <table className="glt">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Strike</th>
                    <th>APR</th>
                    <th>Gate</th>
                    <th>Score</th>
                    <th>Settle</th>
                    <th>Narasi</th>
                  </tr>
                </thead>
                <tbody>
                  {passedSorted.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => setSelectedCandidate(c)}
                      style={{ cursor: 'pointer' }}
                      title="Klik untuk detail gate"
                    >
                      <td>
                        <span style={{ fontWeight: 600, color: 'var(--gl-text)' }}>{c.underlying}</span>{' '}
                        <span className={`gl-badge gl-badge--${c.option_type === 'PUT' ? 'info' : 'warn'}`}
                          title={getOptionLabel(c.option_type)}>
                          {getOptionIcon(c.option_type)} {c.option_type}
                        </span>
                        <span style={{ fontSize: 10, color: 'var(--gl-text-faint)', marginLeft: 4 }}>
                          {getOptionLabel(c.option_type)}
                        </span>
                      </td>
                      <td>{fmt.number(c.strike_price, 0)}</td>
                      <td style={{ color: 'var(--gl-emerald)', fontWeight: 600 }}>{fmt.percent(c.apr, 2)}</td>
                      <td><GateChips c={c} /></td>
                      <td>
                        <span style={{
                          display: 'inline-flex', padding: '2px 8px', borderRadius: 999,
                          fontSize: 11, fontWeight: 600,
                          background: scoreBg(Number(c.soft_score)), color: scoreColor(Number(c.soft_score)),
                        }}>
                          {fmt.score(c.soft_score)}
                        </span>
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmt.countdown(c.settle_date)}</td>
                      <td style={{ fontSize: 11, maxWidth: 250, color: 'var(--gl-text-muted)' }}>
                        {c.narrative ? (
                          c.narrative.slice(0, 70) + (c.narrative.length > 70 ? '…' : '')
                        ) : (
                          <span style={{ color: 'var(--gl-text-faint)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p style={{ fontSize: 11, color: 'var(--gl-text-faint)', marginTop: 12, marginBottom: 0, lineHeight: 1.45 }}>
              Score makin <strong>rendah</strong> makin aman (0–100). APR tinggi bukan prioritas.
            </p>
          </div>

          {/* REJECTED */}
          <div className="g-card rvl rvl-1">
            <div className="gl-label">
              Ditolak{' '}
              <span style={{ color: 'var(--gl-text-faint)', fontWeight: 400 }}>
                · {rejectedN} di cycle · tampil max {Math.min(rejected.length, 100)} baris
              </span>
            </div>
            {rejected.length === 0 ? (
              <div className="gl-empty">Tidak ada yang ditolak (atau cycle kosong).</div>
            ) : (
              <table className="glt">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Strike</th>
                    <th>Type</th>
                    <th>Gate</th>
                    <th>Score</th>
                    <th>Alasan</th>
                  </tr>
                </thead>
                <tbody>
                  {rejected.slice(0, 100).map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => setSelectedCandidate(c)}
                      style={{ cursor: 'pointer' }}
                      title="Klik untuk detail gate"
                    >
                      <td>
                        <span style={{ fontWeight: 500, color: 'var(--gl-text)' }}>{c.underlying}</span>
                      </td>
                      <td>{fmt.number(c.strike_price, 0)}</td>
                      <td>
                        <span className="gl-badge gl-badge--neutral" title={getOptionLabel(c.option_type)}>
                          {getOptionIcon(c.option_type)} {c.option_type}
                        </span>
                        <span style={{ fontSize: 10, color: 'var(--gl-text-faint)', marginLeft: 4 }}>
                          {getOptionLabel(c.option_type)}
                        </span>
                      </td>
                      <td><GateChips c={c} /></td>
                      <td>
                        <span style={{
                          display: 'inline-flex', padding: '2px 8px', borderRadius: 999,
                          fontSize: 11, fontWeight: 600,
                          background: scoreBg(Number(c.soft_score)), color: scoreColor(Number(c.soft_score)),
                        }}>
                          {fmt.score(c.soft_score)}
                        </span>
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--gl-text-faint)', fontFamily: 'var(--font-mono)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.reject_reason}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        /* ===== PAIR VIEW ===== */
        <>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--gl-text)' }}>Per Pair Analytics</div>
            <div style={{ fontSize: 12, color: 'var(--gl-text-muted)', marginTop: 4 }}>
              Analisis performa filter Nevi per underlying pair (data multi-cycle).
            </div>
          </div>

          {pairLoading ? (
            <div className="gl-empty">Memuat…</div>
          ) : pairs.length === 0 ? (
            <div className="gl-empty">Belum ada data. Minta Nevi analisis dulu dari Ringkasan.</div>
          ) : (
            <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
              {pairs.map(([pair, st]) => (
                <PairCard key={pair} pair={pair} stats={st} totalCandidates={pairData?.candidates?.length || 0} />
              ))}
            </div>
          )}

          <div style={{ fontSize: 11, color: 'var(--gl-text-faint)', marginTop: 16, lineHeight: 1.5 }}>
            Data dari hasil filter Nevi — bukan realisasi settlement. Untuk P&L riil, lihat halaman Posisi.
          </div>
        </>
      )}
    </div>
  );
}
